import { useContext, useEffect, useMemo, useRef, useState } from 'react'

import moment from 'moment'
import { useSWRInfinite } from 'swr'

import { MetricsApi } from 'api'
import { fetchAccountsCount, fetchPagedData } from 'api/AccountActivityAPI'
import { PagedResponse } from 'api/AccountActivityAPI/types'
import { DateRange, getDefaultRange } from 'components/DateRangePicker'
import { CurrentUserContext } from 'context/currentUserContext'
import { SortContext } from 'context/sortContext'
import { useAppSelector } from 'hooks/useRedux'
import { AccountActivity, AccountActivitySortValue } from 'modules/accountActivity/domain'
import { FilterableEntity,FilterPage } from 'utils/filters'
import { captureExceptionWithMessage } from 'utils/logRocket'

import {
  AccountOpportunityFilter,
  AccountOpportunityFilterType
} from './../pages/ActivityDashboard/AccountOpportunityContext'
import { getCurrentState, PagedResponseData, useTransformedFilters } from './paginationAndApiUtils'

let loadTimeMetricSent = false

const getBaseUrl = (pageSize: number, pageCursor: number) => {
  return `/v2/accounts/search_with_activities/?pageSize=${pageSize}&pageCursor=${pageCursor}&includeCount=false`
}

export type UseAccountsSortingType = {
  desc: boolean
  id: AccountActivitySortValue
}

export const useAccounts = ({
  pageSize = 25,
  pageCursor = 0,
  opportunities = AccountOpportunityFilter.ALL
}: {
  pageSize?: number
  pageCursor?: number
  opportunities?: AccountOpportunityFilterType
}): PagedResponseData<AccountActivity> => {
  const [resultSize, setResultSize] = useState<number | undefined>()

  const currentUser = useContext(CurrentUserContext)

  const { accountsSort, accountsDescending } = useContext(SortContext)

  const { filterGroupsByPage } = useAppSelector(({ filters }) => filters)

  const hasPageVisitedFilter = filterGroupsByPage[FilterPage.ACCOUNTS].some(group =>
    group.some(filter => filter.filterType === FilterableEntity.PAGE_URL)
  )

  const PAGE_VISITED_RESTRICTED_ORG_IDS = [1524058]
  const customDateRange = useMemo(
    () =>
      !!currentUser?.orgId && PAGE_VISITED_RESTRICTED_ORG_IDS.includes(currentUser.orgId) && hasPageVisitedFilter
        ? {
            endDate: moment().endOf('day'),
            startDate: moment().startOf('day').subtract(14, 'days')
          }
        : getDefaultRange(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUser?.orgId, hasPageVisitedFilter]
  )

  const { searchQuery, dateRange, serializableFilterGroups } = useTransformedFilters(
    FilterPage.ACCOUNTS,
    currentUser?.id,
    customDateRange,
    opportunities
  )

  const timeOfInitialRender = useRef(window.performance?.now())

  //TODO @cmcgorrill update to use react-query when tackling the next-gen prospector UX
  const { data, error, mutate, size, setSize } = useSWRInfinite<PagedResponse<AccountActivity>>(
    (_: number, previousPageData: PagedResponse<AccountActivity> | null) => {
      if (!currentUser || (previousPageData && !previousPageData.nextPagePath)) {
        return null
      }

      const baseUrl = getBaseUrl(pageSize, pageCursor)
      const serializedFilterGroups = JSON.stringify(serializableFilterGroups)

      return [
        baseUrl,
        accountsSort,
        accountsDescending,
        searchQuery,
        dateRange,
        serializedFilterGroups
      ]
    },
    (
      url: string,
      accountsSort: AccountActivitySortValue | undefined,
      accountsDescending: boolean | undefined,
      searchQuery: string | undefined,
      dateRange: DateRange,
      stringifiedFilterGroups: string
    ) => {
      return fetchPagedData<AccountActivity>({
        url,
        sortBy: accountsSort,
        descending: accountsDescending,
        accountName: searchQuery,
        dateRange: dateRange,
        filterGroups: JSON.parse(stringifiedFilterGroups)
      })
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      loadingTimeout: 30_000,
      onSuccess: () => {
        if (loadTimeMetricSent) {
          return
        }

        MetricsApi.post('/monitoring/metrics/add', {
          value: window.performance.now() - timeOfInitialRender.current,
          metric: 'target-accounts-first-rows-displayed',
          dimensions: {
            type: 'bucket',
            buckets: '500,1000,2000,4000,8000'
          }
        })

        loadTimeMetricSent = true
      },
      onLoadingSlow: () => {
        MetricsApi.post('/monitoring/metrics/add', {
          metric: `target-accounts-loading-slow`,
          value: 1,
          dimensions: {
            type: 'counter'
          }
        })
      },
      onError: () => {
        captureExceptionWithMessage('Error fetching paged account data', error)
        MetricsApi.post('/monitoring/metrics/add', {
          metric: `target-accounts-error`,
          value: 1,
          dimensions: {
            type: 'counter'
          }
        })
      }
    }
  )

  //TODO @cmcgorrill update to use react-query when tackling the next-gen prospector UX
  const {
    data: countData,
    error: countError,
    size: countSize,
    setSize: setCountSize
  } = useSWRInfinite<any>(
    () => {
      const stringifiedFilterGroups = JSON.stringify(serializableFilterGroups)
      const cursor = pageCursor ? pageSize * pageCursor : pageCursor

      return [
        accountsSort,
        accountsDescending,
        searchQuery,
        dateRange,
        stringifiedFilterGroups,
        pageSize,
        cursor
      ]
    },
    (
      accountsSort: AccountActivitySortValue | undefined,
      accountsDescending: boolean | undefined,
      searchQuery: string | undefined,
      dateRange: DateRange,
      stringifiedFilterGroups: string,
      pageSize: number,
      cursor: number
    ) =>
      fetchAccountsCount({
        sortBy: accountsSort,
        descending: accountsDescending,
        accountName: searchQuery,
        dateRange: dateRange,
        filterGroups: JSON.parse(stringifiedFilterGroups),
        pageSize: pageSize,
        pageCursor: cursor
      }),
    {
      onError: error => {
        captureExceptionWithMessage('Error fetching account count data', error)
      },
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      loadingTimeout: 30_000
    }
  )

  useEffect(() => {
    if (countSize && !countData) {
      setResultSize(countSize * pageSize)
    }
  }, [countSize, pageSize, countData, setCountSize])

  return {
    ...getCurrentState<AccountActivity>(data, error, size),
    resultSize: resultSize,
    countData: countError ? undefined : countData && countData[0],
    error: countError || error,
    mutate,
    size,
    setSize
  }
}
